import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const exportToPDF = async (elementId = 'resume-preview', fileName = 'resume.pdf') => {
  try {
    // Get the preview element
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Preview element with ID '${elementId}' not found`);
    }

    // Create a clone for PDF generation
    const clone = element.cloneNode(true);
    
    // Force white background on the root element and set darkest text
    clone.style.cssText = `
      width: 210mm;
      min-height: 297mm;
      background-color: #ffffff !important;
      background: #ffffff !important;
      color: #000000 !important;
      font-weight: 500 !important;
      margin: 0 auto;
      padding: 40px;
      position: relative;
      box-sizing: border-box;
      aspect-ratio: 1/1.4142;
    `;

    // Remove backgrounds and ensure maximum contrast text
    function makeWhiteBackgroundDarkText(element) {
      element.style.backgroundColor = '#ffffff';
      element.style.background = '#ffffff';
      element.style.color = '#000000';
      element.style.opacity = '1';
      element.style.fontWeight = element.tagName.match(/^H[1-6]$/) ? '900' : '500';
      element.style.textShadow = 'none';
      element.classList.remove(
        'bg-gradient-to-b', 'from-gray-900', 'to-black',
        'bg-gradient-to-r', 'from-blue-400', 'to-purple-500',
        'backdrop-blur-xl', 'bg-gray-900/50', 'text-gray-400',
        'text-gray-500', 'text-gray-600', 'text-light', 'text-muted'
      );
    }

    // Apply white background and darkest text to all elements
    const allElements = clone.getElementsByTagName('*');
    for (const el of allElements) {
      makeWhiteBackgroundDarkText(el);
      el.style.cssText += ';background-color: #ffffff !important; color: #000000 !important; opacity: 1 !important;';
    }

    // Specifically target and clean the cover letter container
    const coverLetterContainer = clone.querySelector('#cover-letter-preview');
    if (coverLetterContainer) {
      makeWhiteBackgroundDarkText(coverLetterContainer);
      coverLetterContainer.style.cssText += ';background-color: #ffffff !important; color: #000000 !important; opacity: 1 !important;';
    }

    // Clean any parent containers
    const containers = clone.querySelectorAll('.bg-gray-900\\/50, .backdrop-blur-xl, .min-h-screen');
    containers.forEach(container => {
      makeWhiteBackgroundDarkText(container);
      container.style.cssText += ';background-color: #ffffff !important; color: #000000 !important; opacity: 1 !important;';
    });

    // Style the name element (h1) with extra bold black color
    const nameElement = clone.querySelector('h1');
    if (nameElement) {
      nameElement.style.cssText = `
        font-size: 36px;
        color: #000000 !important;
        font-weight: 900 !important;
        margin-bottom: 1.5rem;
        background: #ffffff !important;
        -webkit-background-clip: initial;
        -webkit-text-fill-color: #000000;
        opacity: 1;
        letter-spacing: -0.5px;
      `;
    }

    // Apply clean styles with maximum contrast
    const previewStyles = {
      '#contact-info': {
        display: 'flex',
        gap: '24px',
        marginTop: '16px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontWeight: '500'
      },
      '.contact-item': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 14px',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: '14px',
        fontWeight: '600',
        border: '1.5px solid #000000'
      },
      'h2': {
        fontSize: '26px',
        fontWeight: '900',
        color: '#000000',
        borderBottom: '2px solid #000000',
        paddingBottom: '8px',
        marginBottom: '20px',
        backgroundColor: '#ffffff',
        letterSpacing: '-0.5px'
      },
      '.work-item': {
        marginBottom: '24px',
        paddingLeft: '16px',
        borderLeft: '2px solid #000000',
        color: '#000000'
      },
      '.work-title': {
        fontSize: '20px',
        fontWeight: '800',
        color: '#000000',
        letterSpacing: '-0.3px'
      },
      '.work-company': {
        color: '#000000',
        fontWeight: '700'
      },
      '.work-date': {
        fontSize: '14px',
        color: '#000000',
        backgroundColor: '#ffffff',
        padding: '4px 12px',
        borderRadius: '4px',
        fontWeight: '600',
        border: '1.5px solid #000000'
      },
      '.work-description': {
        marginTop: '8px',
        color: '#000000',
        lineHeight: '1.6',
        fontWeight: '500'
      },
      '.skills-container': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        color: '#000000'
      },
      '.skill-item': {
        backgroundColor: '#ffffff',
        color: '#000000',
        padding: '6px 14px',
        borderRadius: '4px',
        fontSize: '14px',
        border: '1.5px solid #000000',
        fontWeight: '600'
      },
      '.project-item': {
        marginBottom: '24px',
        paddingLeft: '16px',
        borderLeft: '2px solid #000000',
        color: '#000000'
      },
      '.project-title': {
        fontSize: '20px',
        fontWeight: '800',
        color: '#000000',
        letterSpacing: '-0.3px'
      },
      '.project-tech': {
        color: '#000000',
        fontWeight: '700'
      }
    };

    // Apply styles to clone
    Object.entries(previewStyles).forEach(([selector, styles]) => {
      const elements = clone.querySelectorAll(selector);
      elements.forEach(element => {
        Object.assign(element.style, styles);
      });
    });

    // Temporarily add to document for rendering
    document.body.appendChild(clone);

    // Wait longer for fonts and rendering
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Convert the clone to canvas
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    // Remove the clone from document
    document.body.removeChild(clone);

    // Convert to PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

    // Save the PDF
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};